import { translated } from '@/lib/api/common'

import {
  ApprovalOutcome,
  ApprovalRequirement,
  CatalogueState,
  ExceptionResolution,
  ExceptionStatus,
  RegisterHealth,
  SessionStatus,
  ShiftReportType,
  TableState,
} from '../types'
import type { PosBranchRef } from '../types'

/**
 * Everything the POS screens turn into words or colour.
 *
 * In one file because the same vocabulary appears on five tabs — a register's
 * "Offline" chip and a session's "Open" chip must read as the same kind of
 * thing, and the fastest way to lose that is to style each table's chips where
 * that table is written.
 */

export function branchLabel(branch: PosBranchRef | null | undefined): string {
  return translated(branch?.name, 'Branch')
}

/* -------------------------------------------------------------------------- */
/* Time                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * "2 min ago", "12 min ago", "Yesterday 09:10".
 *
 * Relative up to a day because that is the question being asked — a register's
 * last heartbeat matters as "how long has it been quiet", not as a clock time.
 * Past a day it flips to absolute: "36 hours ago" is arithmetic nobody wants
 * to do standing at a counter.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'

  const then = new Date(iso)

  if (Number.isNaN(then.getTime())) return '—'

  const seconds = Math.round((Date.now() - then.getTime()) / 1000)

  if (seconds < 45) return 'Just now'
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`
  if (seconds < 6 * 3600) return `${Math.round(seconds / 3600)} h ago`

  const time = then.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  const startOfToday = new Date()

  startOfToday.setHours(0, 0, 0, 0)

  if (then >= startOfToday) return `Today ${time}`

  const startOfYesterday = new Date(startOfToday)

  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  if (then >= startOfYesterday) return `Yesterday ${time}`

  const days = Math.round((startOfToday.getTime() - then.getTime()) / 86_400_000)

  if (days < 7) return `${days} days ago`

  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * "30 Sep, 14:32" — a clock time, not a distance from now.
 *
 * The counterpart to `relativeTime`, and the right one for anything that will be
 * read back later or read out to somebody else. "2 h ago" on a report a manager
 * is signing off is useless the moment the page is left open, and useless again
 * the next morning.
 */
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'

  const at = new Date(iso)

  if (Number.isNaN(at.getTime())) return '—'

  return at.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "14:32" — the time alone, for a pair that already shares a date. */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return '—'

  const at = new Date(iso)

  if (Number.isNaN(at.getTime())) return '—'

  return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/** "22m", "1h 04m" — how long a party has been at a table. */
export function elapsedLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'

  const minutes = Math.max(0, Math.floor(seconds / 60))

  if (minutes < 60) return `${minutes}m`

  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/* -------------------------------------------------------------------------- */
/* Money                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `$34.50`, and `–$4.40` for a short drawer.
 *
 * A true minus sign (U+2013 in the caller's font stack is close enough at this
 * size) rather than a hyphen, and always two decimals: a column of takings that
 * mixes `$12` with `$12.00` cannot be scanned down.
 */
export function money(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—'

  const sign = amount < 0 ? '−' : ''

  return `${sign}$${Math.abs(amount).toFixed(2)}`
}

/**
 * `47%` from the API's 0–1 share.
 *
 * Whole percent, and it deliberately does not re-derive the ratio from the two
 * amounts beside it: the API computes every share so that two screens cannot
 * round the same split into two different pie charts.
 */
export function percent(share: number | null | undefined): string {
  if (share === null || share === undefined) return '—'

  return `${Math.round(share * 100)}%`
}

/** `42` — a count, never a dash, because zero of something is a real answer. */
export function count(value: number | null | undefined): string {
  return String(value ?? 0)
}

/** A variance of exactly zero reads as `$0.00`, never as a dash. */
export function varianceLabel(variance: number | null | undefined): string {
  return variance === null || variance === undefined ? '—' : money(variance)
}

/* -------------------------------------------------------------------------- */
/* Chips                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * What a status chip says, and which tone it says it in.
 *
 * A TONE, not a set of Tailwind classes. Every other module in the app paints a
 * status with HeroUI's soft `Chip` — Orders, Products, Discounts, Business,
 * Storefront, Modifiers and Tables all do — so these name the same five tones
 * that component already understands rather than hand-mixing a seventh palette
 * that drifts from the theme the first time the brand colour moves.
 *
 * The vocabulary is what matters here and the paint is not: keeping the labels
 * and the severity in one file is what stops a register's "Offline" and a
 * session's "Open" from reading as different kinds of statement.
 */
export type ChipTone = 'default' | 'success' | 'warning' | 'danger' | 'accent'

export interface ChipStyle {
  label: string
  tone: ChipTone
}

const NEUTRAL: ChipStyle = { label: '—', tone: 'default' }

export const registerHealthStyles: Record<RegisterHealth, ChipStyle> = {
  [RegisterHealth.Active]: { label: 'Active', tone: 'success' },
  // Danger, not warning: a till that has gone quiet mid-service is somebody
  // walking over to look at it, not something to note and move past.
  [RegisterHealth.Offline]: { label: 'Offline', tone: 'danger' },
  [RegisterHealth.NeverSignedIn]: { label: 'Not signed in yet', tone: 'default' },
}

export const catalogueStyles: Record<CatalogueState, ChipStyle> = {
  [CatalogueState.Current]: { label: 'Current', tone: 'success' },
  // Warning: the till is selling yesterday's menu. Wrong, but it is still
  // selling, which is a different problem from being switched off.
  [CatalogueState.Behind]: { label: 'Behind', tone: 'warning' },
  [CatalogueState.Offline]: { label: 'Offline', tone: 'default' },
  [CatalogueState.Unknown]: NEUTRAL,
}

export const sessionStatusStyles: Record<SessionStatus, ChipStyle> = {
  // Accent rather than success. An open drawer is not a healthy state, it is an
  // unfinished one — green here would read as "all counted".
  [SessionStatus.Open]: { label: 'Open', tone: 'accent' },
  [SessionStatus.Closed]: { label: 'Closed', tone: 'default' },
  [SessionStatus.ForceClosed]: { label: 'Force-closed', tone: 'warning' },
}

/**
 * Which document you are looking at.
 *
 * Accent for an X and success for a Z, and the pairing is the point: an X wears
 * the same tone an open session does, because it is a provisional reading of
 * something unfinished. Green is the one that is signed off. A manager should be
 * able to tell the two apart from across the desk, because one of them can be
 * taken again and the other never can.
 */
export const shiftReportStyles: Record<ShiftReportType, ChipStyle> = {
  [ShiftReportType.X]: { label: 'X · reading', tone: 'accent' },
  [ShiftReportType.Z]: { label: 'Z · final', tone: 'success' },
}

export const exceptionStatusStyles: Record<ExceptionStatus, ChipStyle> = {
  [ExceptionStatus.Open]: { label: 'Open', tone: 'warning' },
  [ExceptionStatus.Resolved]: { label: 'Resolved', tone: 'success' },
  [ExceptionStatus.Discarded]: { label: 'Discarded', tone: 'default' },
  // Its own label, not "Resolved": nobody judged it, and a manager reviewing
  // the month should be able to see which ones the server settled alone.
  [ExceptionStatus.AutoResolved]: { label: 'Auto-resolved', tone: 'success' },
}

export const approvalOutcomeStyles: Record<ApprovalOutcome, ChipStyle> = {
  [ApprovalOutcome.Granted]: { label: 'Granted', tone: 'success' },
  [ApprovalOutcome.Denied]: { label: 'Denied', tone: 'danger' },
  [ApprovalOutcome.Expired]: { label: 'Expired', tone: 'default' },
}

export function chipStyle<T extends string>(
  styles: Record<string, ChipStyle>,
  key: T | null | undefined,
): ChipStyle {
  return (key && styles[key]) || NEUTRAL
}

/* -------------------------------------------------------------------------- */
/* The floor                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A table's card: its wash, its border and what its status line says.
 *
 * Colour carries the meaning here in a way it does not in a table of chips —
 * a host reads the grid from across a room — so each state gets a full card
 * tint rather than a dot.
 */
export interface TableCardStyle {
  label: string
  card: string
  dot: string
  text: string
}

export const tableStateStyles: Record<TableState, TableCardStyle> = {
  [TableState.Available]: {
    label: 'Available',
    card: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-400',
  },
  [TableState.Occupied]: {
    label: 'Occupied',
    card: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
  },
  [TableState.Ordered]: {
    label: 'Ordered',
    card: 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-900',
    dot: 'bg-orange-500',
    text: 'text-orange-700 dark:text-orange-400',
  },
  // Blue, and it breaks the warm ramp on purpose: served is the table a waiter
  // should be walking towards with a bill, not a hotter shade of occupied.
  [TableState.Served]: {
    label: 'Served',
    card: 'bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-900',
    dot: 'bg-sky-500',
    text: 'text-sky-700 dark:text-sky-400',
  },
  [TableState.Dirty]: {
    label: 'Dirty',
    card: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900',
    dot: 'bg-red-500',
    text: 'text-red-700 dark:text-red-400',
  },
  [TableState.Disabled]: {
    label: 'Out of service',
    card: 'bg-surface border-border opacity-70',
    dot: 'bg-zinc-400',
    text: 'text-muted',
  },
}

export function tableCardStyle(status: string): TableCardStyle {
  return tableStateStyles[status as TableState] ?? tableStateStyles[TableState.Available]
}

/**
 * How long a table may sit in one state before the grid says so.
 *
 * Forty minutes on a served table is a bill nobody has brought; ten on a dirty
 * one is a cover the room is not earning. Both are worth a manager's eye, and
 * neither is an error — hence a quiet outline rather than a chip.
 */
const ATTENTION_AFTER_SECONDS: Partial<Record<TableState, number>> = {
  [TableState.Served]: 40 * 60,
  [TableState.Dirty]: 10 * 60,
}

export function needsAttention(status: string, elapsedSeconds: number | null): boolean {
  const threshold = ATTENTION_AFTER_SECONDS[status as TableState]

  return Boolean(threshold && elapsedSeconds !== null && elapsedSeconds >= threshold)
}

/* -------------------------------------------------------------------------- */
/* Approvals                                                                  */
/* -------------------------------------------------------------------------- */

export const requirementLabels: Record<ApprovalRequirement, string> = {
  [ApprovalRequirement.Free]: 'Free',
  [ApprovalRequirement.Manager]: 'Manager',
}

export const resolutionLabels: Record<ExceptionResolution, string> = {
  [ExceptionResolution.KeptUploaded]: 'Book the uploaded amount',
  [ExceptionResolution.KeptExisting]: 'Keep what is already on file',
  [ExceptionResolution.Dropped]: 'Do not book it at all',
}

/** What each choice actually does, for the line under the radio. */
export const resolutionDescriptions: Record<ExceptionResolution, string> = {
  [ExceptionResolution.KeptUploaded]:
    'The till’s figure becomes the record. Use this when the register is the one that saw the sale.',
  [ExceptionResolution.KeptExisting]:
    'The upload is dropped and the existing order stands unchanged.',
  [ExceptionResolution.Dropped]:
    'Nothing is booked. Use this when the sale was voided or re-keyed by hand.',
}
